const normalizeValue = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return decodeURIComponent(trimmed);
  } catch (_) {
    return trimmed;
  }
};

const parseFingerprint = (fingerprint) => {
  const normalizedFingerprint = normalizeValue(fingerprint);
  if (!normalizedFingerprint) {
    return {
      normalizedFingerprint: null,
      installId: null,
      baseFingerprint: null,
    };
  }

  const match = normalizedFingerprint.match(/_(DEV-[A-Za-z0-9-]+)$/i);
  const installId = match ? match[1] : null;
  const baseFingerprint = match ? normalizedFingerprint.slice(0, -match[0].length) : normalizedFingerprint;

  return {
    normalizedFingerprint,
    installId,
    baseFingerprint,
  };
};

const fingerprintsMatchSemantically = (left, right) => {
  const parsedLeft = parseFingerprint(left);
  const parsedRight = parseFingerprint(right);

  if (!parsedLeft.normalizedFingerprint || !parsedRight.normalizedFingerprint) {
    return false;
  }

  if (
    parsedLeft.normalizedFingerprint.toLowerCase() ===
    parsedRight.normalizedFingerprint.toLowerCase()
  ) {
    return true;
  }

  if (
    !parsedLeft.baseFingerprint ||
    !parsedRight.baseFingerprint ||
    parsedLeft.baseFingerprint.toLowerCase() !== parsedRight.baseFingerprint.toLowerCase()
  ) {
    return false;
  }

  return (
    !parsedLeft.installId ||
    !parsedRight.installId ||
    parsedLeft.installId.toLowerCase() === parsedRight.installId.toLowerCase()
  );
};

export function chooseBootstrapFingerprint({ urlFingerprint, storedFingerprint }) {
  const normalizedUrlFingerprint = normalizeValue(urlFingerprint);
  const normalizedStoredFingerprint = normalizeValue(storedFingerprint);

  if (normalizedUrlFingerprint) {
    return {
      fingerprint: normalizedUrlFingerprint,
      source: 'url',
      shouldResetStoredIdentity:
        !!normalizedStoredFingerprint &&
        !fingerprintsMatchSemantically(normalizedStoredFingerprint, normalizedUrlFingerprint),
    };
  }

  if (normalizedStoredFingerprint) {
    return {
      fingerprint: normalizedStoredFingerprint,
      source: 'stored',
      shouldResetStoredIdentity: false,
    };
  }

  return {
    fingerprint: null,
    source: null,
    shouldResetStoredIdentity: false,
  };
}

export function shouldUseStoredDeviceToken({
  currentFingerprint,
  storedTokenFingerprint,
  hasUrlFingerprint,
}) {
  if (!hasUrlFingerprint) {
    return true;
  }

  const normalizedCurrentFingerprint = normalizeValue(currentFingerprint);
  const normalizedStoredTokenFingerprint = normalizeValue(storedTokenFingerprint);

  if (!normalizedCurrentFingerprint || !normalizedStoredTokenFingerprint) {
    return false;
  }

  return fingerprintsMatchSemantically(
    normalizedCurrentFingerprint,
    normalizedStoredTokenFingerprint
  );
}

export function shouldClearNativeCachedIdentity({
  isNativeCtvBootstrap,
  strictInstall,
  urlDeviceToken,
  cachedScreenId,
}) {
  if (!isNativeCtvBootstrap || !strictInstall) {
    return false;
  }

  if (normalizeValue(urlDeviceToken)) {
    return false;
  }

  return !!normalizeValue(cachedScreenId);
}

export function getReusableCachedScreenId({
  currentFingerprint,
  cachedScreenId,
  cachedScreenFingerprint,
  hasUrlFingerprint,
}) {
  const normalizedCachedScreenId = normalizeValue(cachedScreenId);
  if (!normalizedCachedScreenId) {
    return null;
  }

  if (!hasUrlFingerprint) {
    return normalizedCachedScreenId;
  }

  const normalizedCurrentFingerprint = normalizeValue(currentFingerprint);
  const normalizedCachedScreenFingerprint = normalizeValue(cachedScreenFingerprint);

  if (
    !normalizedCurrentFingerprint ||
    !normalizedCachedScreenFingerprint ||
    !fingerprintsMatchSemantically(
      normalizedCurrentFingerprint,
      normalizedCachedScreenFingerprint
    )
  ) {
    return null;
  }

  return normalizedCachedScreenId;
}

export function getAttachedScreenId(attachData) {
  if (!attachData || typeof attachData !== 'object') {
    return null;
  }

  return normalizeValue(
    attachData.screen_id ||
    attachData.screenId ||
    attachData.earnerscreen_id ||
    attachData.earner_screen_id
  );
}
